// Máquina de estados del signup (XState v5) — AUTORIDAD ÚNICA del ciclo del
// form: editar → validar → (elegir plan) → crear → desenlace.
//
// El bug que motivó esto: el submit trataba "no pude verificar el email" (async
// con error/429) como "está OK" → dejaba avanzar con una cuenta existente hasta
// elegir plan, y ahí el alta real chocaba el rate-limit ("Demasiados intentos").
// Colapsaba tres valores (true|false|UNKNOWN) en dos.
//
// INVARIANTE (verificado por signup-machine.test):
//   1. Tri-valuado, FAIL-CLOSED en `unknown`. El check async resuelve a
//      exists|undeliverable|available; error/red/429/timeout ⇒ `unknown` ⇒
//      BLOQUEA con mensaje propio, NUNCA pasa de largo.
//   2. Fuente única de decisión: `evaluateGate` (puro) compone Zod (no-email) +
//      la cascada de email + el estado async. Un solo lugar decide.
//   3. `creating` es INALCANZABLE salvo desde `details.validating` (ok, sin
//      planes) o `plan.choosing` (que solo se alcanza desde validating ok). El
//      grafo lo garantiza; no hay transición libre a `creating`.

import { setup, assign, fromPromise } from 'xstate';
import {
  deriveEmailVerdict,
  decideSubmit,
  normalizeEmail,
  EMAIL_EXISTS_MSG,
  type EmailVerdict,
  type EmailBackendState,
} from './emailValidation';
import { SignupDetailsSchema, mapSchemaIssues, type FieldKey } from './signup-schema';
import {
  checkEmail as apiCheckEmail,
  signup as apiSignup,
  login as apiLogin,
  isConsentRequired,
  ApiError,
  type Plan,
  type SignupParams,
  type SignupRequiredFavoriteConfig,
} from './api';
import { makeVerifyNonce, setVerifyCookie } from './verify-nonce';
import type { AuthParamsResult } from './auth-params';
import type { SelectedFavorite } from '@/components/SearchableFavoriteSelect';

// Mensaje del estado `unknown` (fail-closed): el check no pudo resolver.
export const EMAIL_UNKNOWN_MSG = 'No pudimos verificar tu email. Intenta más tarde.';

// Orden VISUAL de los campos (para elegir el primer error y hacerle scroll).
const FIELD_ORDER: FieldKey[] = [
  'first_name',
  'last_name',
  'email',
  'email_confirm',
  'password',
  'terms',
];

// ---------------------------------------------------------------------------
// Tipos de context y eventos
// ---------------------------------------------------------------------------

export interface SignupFormValues {
  first_name: string;
  last_name: string;
  email: string;
  email_confirm: string;
  password: string;
  terms_accepted: boolean;
  marketing_opt_in: boolean;
  favorites: Record<string, SelectedFavorite | null>;
}

export type GateStatus = 'idle' | 'invalid' | 'need-backend' | 'ready';

export interface SignupContext {
  // Config (partnerId/authz al crear; plansEnabled/requiredFavorites vía evento).
  partnerId: string;
  authz: AuthParamsResult;
  plansEnabled: boolean;
  requiredFavorites: SignupRequiredFavoriteConfig[];
  // Valores del form.
  values: SignupFormValues;
  // Veredicto async por email normalizado. SOLO estados terminales
  // (exists|undeliverable|available); un email sin entrada = 'not-run'.
  emailCache: Record<string, EmailBackendState>;
  typoDismissedFor: string | null;
  emailTouched: boolean;
  // Salida de validación.
  fieldErrors: Partial<Record<FieldKey, string>>;
  formError: string | null;
  showRecoverLink: boolean;
  firstErrorField: FieldKey | null;
  missingFavorites: string[];
  // Warning transitorio del email ("no pudimos verificar" / fail-closed). NO es un
  // error de campo (no pinta rojo ni va al banner): se muestra como ítem ámbar en
  // la lista de estado del email y pinta el marco de warning.
  emailWarning: string | null;
  scrollNonce: number;
  gateStatus: GateStatus;
  // Plan.
  selectedPlan: Plan | null;
  // Desenlaces.
  submittedEmail: string | null;
  pendingPaidPlan: Plan | null;
  redirectTo: string | null;
  payToken: string | null;
  /** Falla del re-chequeo "ya validé mi correo" que NO es "todavía no". */
  verifyError: string | null;
}

export interface SignupInput {
  partnerId: string;
  authz: AuthParamsResult;
}

type TextField = 'first_name' | 'last_name' | 'email' | 'email_confirm' | 'password';

export type SignupEvent =
  | { type: 'CONFIG_LOADED'; plansEnabled: boolean; requiredFavorites: SignupRequiredFavoriteConfig[] }
  | { type: 'SET_FIELD'; field: TextField; value: string }
  | { type: 'TOGGLE'; field: 'terms_accepted' | 'marketing_opt_in'; value: boolean }
  | { type: 'SET_FAVORITE'; favoriteTypeId: string; value: SelectedFavorite | null }
  | { type: 'BLUR_EMAIL' }
  | { type: 'ACCEPT_TYPO' }
  | { type: 'KEEP_TYPO' }
  | { type: 'SUBMIT' }
  | { type: 'SELECT_PLAN'; plan: Plan }
  | { type: 'CONFIRM_PLAN' }
  | { type: 'BACK' }
  | { type: 'CREATE_ANOTHER' }
  // "Ya validé mi correo": reintenta el login para refrescar el estado sin
  // esperar a que el usuario vuelva solo a la pantalla de login.
  | { type: 'VERIFY_CHECK' };

type SignupResult = Awaited<ReturnType<typeof apiSignup>>;
/**
 * Desenlace del re-chequeo post-verificación. `verified_needs_consent` es el
 * caso en que el login pasó el gate pero el backend pide re-aceptar T&C: el
 * email ESTÁ verificado (el gate corre antes), solo que el cierre del flujo
 * necesita el interstitial, que vive en la pantalla de login.
 */
type VerifyCheckResult =
  | { kind: 'redirect'; redirectTo: string }
  | { kind: 'verified_needs_consent' };
type CheckResult = { email: string; state: EmailBackendState };
interface SignupActorInput {
  partnerId: string;
  authz: AuthParamsResult;
  params: SignupParams;
}

// ---------------------------------------------------------------------------
// Núcleo puro (testeable sin la máquina)
// ---------------------------------------------------------------------------

function emailBackendOf(context: SignupContext): EmailBackendState {
  return context.emailCache[normalizeEmail(context.values.email)] ?? 'not-run';
}

/** Veredicto de email derivado del context (misma fuente para gate y render). */
export function emailVerdictOf(context: SignupContext): EmailVerdict {
  return deriveEmailVerdict({
    email: context.values.email,
    confirm: context.values.email_confirm,
    typoDismissedFor: context.typoDismissedFor,
    backend: emailBackendOf(context),
  });
}

export interface GateResult {
  status: 'invalid' | 'need-backend' | 'ready';
  fieldErrors: Partial<Record<FieldKey, string>>;
  formError: string | null;
  showRecoverLink: boolean;
  firstErrorField: FieldKey | null;
  // favorite_type_ids requeridos que faltan seleccionar (para el contorno rojo
  // del selector de favoritos, que no es un FieldKey fijo).
  missingFavorites: string[];
}

/**
 * EL PUNTO ÚNICO DE DECISIÓN. Puro y total: dado el context, decide si el paso
 * de datos puede avanzar. Compone Zod (no-email) + la cascada de email + los
 * favoritos requeridos. Nunca devuelve 'ready' con un email sin resolver: si el
 * email necesita backend devuelve 'need-backend' (la máquina lo resuelve y
 * re-evalúa); si el backend no es 'available', 'invalid'. `unknown` no existe
 * acá — lo maneja la máquina como fail-closed al fallar el check.
 */
export function evaluateGate(context: SignupContext): GateResult {
  const v = context.values;

  // 1. Campos no-email (Zod).
  const parsed = SignupDetailsSchema.safeParse({
    first_name: v.first_name,
    last_name: v.last_name,
    password: v.password,
    terms: v.terms_accepted,
  });
  const zodErrors = parsed.success ? {} : mapSchemaIssues(parsed.error);

  // 2. Email (cascada sync + estado async cacheado).
  const decision = decideSubmit(emailVerdictOf(context));

  // 3. Ensamblar errores de campo en orden visual.
  const fieldErrors: Partial<Record<FieldKey, string>> = {};
  if (zodErrors.first_name) fieldErrors.first_name = zodErrors.first_name;
  if (zodErrors.last_name) fieldErrors.last_name = zodErrors.last_name;
  if (decision.action === 'block') fieldErrors[decision.field] = decision.message;
  if (zodErrors.password) fieldErrors.password = zodErrors.password;
  if (zodErrors.terms) fieldErrors.terms = zodErrors.terms;

  const errorFields = FIELD_ORDER.filter((f) => fieldErrors[f]);
  if (errorFields.length > 0) {
    const first = errorFields[0]!;
    const firstMsg = fieldErrors[first]!;
    const formError =
      errorFields.length > 1
        ? `${firstMsg} Revisá también los demás campos marcados.`
        : firstMsg;
    return { status: 'invalid', fieldErrors, formError, showRecoverLink: false, firstErrorField: first, missingFavorites: [] };
  }

  // 4. Sin errores de campo → recién ahora, favoritos requeridos.
  const missingFav = context.requiredFavorites.filter(
    (rf) => v.favorites[rf.favorite_type_id] === undefined,
  );
  if (missingFav.length > 0) {
    return {
      status: 'invalid',
      fieldErrors: {},
      formError: 'Elegí tu equipo',
      showRecoverLink: false,
      firstErrorField: null,
      missingFavorites: missingFav.map((rf) => rf.favorite_type_id),
    };
  }

  // 5. ¿El email necesita el check async? (verdict 'unverified'/'checking').
  if (decision.action === 'resolve-backend') {
    return { status: 'need-backend', fieldErrors: {}, formError: null, showRecoverLink: false, firstErrorField: null, missingFavorites: [] };
  }

  // 6. Todo verde (decision.action === 'proceed').
  return { status: 'ready', fieldErrors: {}, formError: null, showRecoverLink: false, firstErrorField: null, missingFavorites: [] };
}

/** Params del alta real, derivados del context (sin verify_nonce; lo agrega el actor). */
function buildSignupParams(context: SignupContext): SignupParams {
  const v = context.values;
  const favoritesPayload = context.requiredFavorites.map((rf) => ({
    favorite_type_id: rf.favorite_type_id,
    favorite_id: v.favorites[rf.favorite_type_id]?.favorite_id ?? null,
  }));
  const authz = context.authz;
  return {
    ...(authz.ok && {
      client_id: authz.params.client_id,
      redirect_uri: authz.params.redirect_uri,
      state: authz.params.state,
      code_challenge: authz.params.code_challenge,
      code_challenge_method: 'S256' as const,
      scope: authz.params.scope,
      ...(authz.params.nonce ? { nonce: authz.params.nonce } : {}),
    }),
    // Capa 3 — submit: la forma canónica es la que viaja al backend, no la
    // que quedó en el state. Ver src/lib/email-normalize.ts.
    email: normalizeEmail(v.email),
    password: v.password,
    first_name: v.first_name,
    last_name: v.last_name,
    terms_accepted: v.terms_accepted,
    marketing_opt_in: v.marketing_opt_in,
    ...(context.selectedPlan ? { plan_id: context.selectedPlan.plan_id } : {}),
    ...(favoritesPayload.length > 0 ? { favorites: favoritesPayload } : {}),
  };
}

/** Mapea (code, message) de un ApiError del alta a mensaje + oferta de recuperar. */
export interface MappedError {
  text: string;
  showRecoverLink: boolean;
}
export function mapSignupError(code: string, message?: string): MappedError {
  switch (code) {
    case 'already_blocked':
    case 'user_exists':
    case 'email_already_registered':
      return { text: 'Ya tenés una cuenta', showRecoverLink: true };
    case 'rate_limited':
      return { text: 'Demasiados intentos. Esperá.', showRecoverLink: false };
    case 'network_error':
      return { text: 'Sin conexión. Vuelve a probar.', showRecoverLink: false };
    case 'internal_error':
      return { text: 'Hubo un error. Vuelve a probar.', showRecoverLink: false };
    case 'user_not_found':
      return { text: 'Enlace inválido. Volvé a la app.', showRecoverLink: false };
    case 'invalid_request_body': {
      const m = (message ?? '').toLowerCase();
      if (m.includes('password') || m.includes('contraseñ')) {
        return { text: 'Revisá la contraseña', showRecoverLink: false };
      }
      if (m.includes('email')) {
        return { text: 'Correo inválido', showRecoverLink: false };
      }
      return { text: 'Revisá los datos', showRecoverLink: false };
    }
    default:
      return { text: 'No pudimos crear la cuenta. Vuelve a probar.', showRecoverLink: false };
  }
}

function isEmailTakenError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  const emailCodes = ['already_blocked', 'user_exists', 'email_already_registered'];
  return emailCodes.includes(err.code) || (err.message ?? '').toLowerCase().includes('email');
}

// ---------------------------------------------------------------------------
// Context inicial
// ---------------------------------------------------------------------------

function initialFormValues(): SignupFormValues {
  return {
    first_name: '',
    last_name: '',
    email: '',
    email_confirm: '',
    password: '',
    terms_accepted: false,
    marketing_opt_in: false,
    favorites: {},
  };
}

function initialContext(input: SignupInput): SignupContext {
  return {
    partnerId: input.partnerId,
    authz: input.authz,
    plansEnabled: false,
    requiredFavorites: [],
    values: initialFormValues(),
    emailCache: {},
    typoDismissedFor: null,
    emailTouched: false,
    fieldErrors: {},
    formError: null,
    showRecoverLink: false,
    firstErrorField: null,
    missingFavorites: [],
    emailWarning: null,
    scrollNonce: 0,
    gateStatus: 'idle',
    selectedPlan: null,
    submittedEmail: null,
    pendingPaidPlan: null,
    redirectTo: null,
    payToken: null,
    verifyError: null,
  };
}

// ---------------------------------------------------------------------------
// La máquina
// ---------------------------------------------------------------------------

export const signupMachineConfig = {
  id: 'signup',
  context: ({ input }: { input: SignupInput }) => initialContext(input),
  initial: 'details',
  states: {
    details: {
      initial: 'editing',
      // Edición de campos + config + typo: a nivel del padre para que apliquen
      // en cualquier sub-estado de `details` (editing/checkingEmail).
      on: {
        CONFIG_LOADED: { actions: 'assignConfig' },
        SET_FIELD: { actions: 'assignField' },
        TOGGLE: { actions: 'assignToggle' },
        SET_FAVORITE: { actions: 'assignFavorite' },
        ACCEPT_TYPO: { target: '.checkingEmail', actions: 'applyTypoFix' },
        KEEP_TYPO: { target: '.checkingEmail', actions: 'keepTyped' },
      },
      states: {
        editing: {
          on: {
            BLUR_EMAIL: [
              { guard: 'emailCheckable', target: 'checkingEmail', actions: 'touchEmail' },
              { actions: 'touchEmail' },
            ],
            SUBMIT: { target: 'validating', actions: 'touchEmail' },
          },
        },
        // Chequeo de aviso temprano (al blur / tras aceptar-descartar typo).
        // NO bloquea ni avanza: solo puebla el cache para el render de EmailStatus.
        checkingEmail: {
          invoke: {
            src: 'checkEmail',
            input: ({ context }: { context: SignupContext }) => ({
              partnerId: context.partnerId,
              email: normalizeEmail(context.values.email),
            }),
            onDone: { target: 'editing', actions: 'cacheEmailResult' },
            onError: { target: 'editing' },
          },
          on: {
            // Se puede enviar mientras corre el aviso: el gate resuelve/reusa.
            SUBMIT: { target: 'validating', actions: 'touchEmail' },
          },
        },
        // GATE. Puro: evalúa y decide. `always` rutea según el resultado.
        validating: {
          entry: 'assignGate',
          always: [
            { guard: 'gateInvalid', target: 'editing', actions: 'bumpScroll' },
            { guard: 'gateNeedBackend', target: 'checkingForSubmit' },
            { guard: 'gateReadyPlans', target: '#signup.plan' },
            { guard: 'gateReadyNoPlans', target: '#signup.creating' },
          ],
        },
        // Resolución async del gate. onDone → vuelve a `validating` (re-evalúa con
        // el cache actualizado, ya terminal → no vuelve a need-backend).
        // onError → FAIL-CLOSED: vuelve a editing con el mensaje 'unknown'.
        checkingForSubmit: {
          invoke: {
            src: 'checkEmail',
            input: ({ context }: { context: SignupContext }) => ({
              partnerId: context.partnerId,
              email: normalizeEmail(context.values.email),
            }),
            onDone: { target: 'validating', actions: 'cacheEmailResult' },
            onError: { target: 'editing', actions: 'setUnknownEmailError' },
          },
        },
      },
    },
    plan: {
      initial: 'choosing',
      states: {
        choosing: {
          on: {
            SELECT_PLAN: { actions: 'assignSelectedPlan' },
            CONFIRM_PLAN: { guard: 'hasSelectedPlan', target: '#signup.creating' },
            BACK: { target: '#signup.details.editing', actions: 'clearFormError' },
          },
        },
      },
    },
    // ALTA REAL. Única in-edge: validating (ok, sin planes) o plan.choosing.
    creating: {
      invoke: {
        src: 'signup',
        input: ({ context }: { context: SignupContext }) => ({
          partnerId: context.partnerId,
          authz: context.authz,
          params: buildSignupParams(context),
        }),
        onDone: [
          { guard: 'signupHasRedirect', target: 'redirecting', actions: 'assignRedirect' },
          { guard: 'signupHasPayToken', target: 'redirecting', actions: 'assignPayToken' },
          { target: 'submitted', actions: 'assignSubmitted' },
        ],
        onError: [
          { guard: 'errIsEmailTaken', target: 'details.editing', actions: 'applyEmailTakenError' },
          { guard: 'plansEnabled', target: 'plan.choosing', actions: 'applyCreateError' },
          { target: 'details.editing', actions: 'applyCreateError' },
        ],
      },
    },
    // El componente navega (window.location) al entrar acá.
    redirecting: {},
    // Muro post-alta: "revisá tu correo". El usuario verifica en otra pestaña
    // (o en el celular) y vuelve acá a apretar "ya validé mi correo", que
    // reintenta el login. Es un re-chequeo REAL contra el gate, no una consulta
    // de estado aparte: si pasa, el usuario entra; si no, sabemos que el gate
    // todavía lo rebota. De paso evita abrir un endpoint de "¿este email está
    // verificado?", que sería un oráculo de existencia de cuentas.
    submitted: {
      initial: 'idle',
      on: { CREATE_ANOTHER: { target: '#signup.details.editing', actions: 'resetForm' } },
      states: {
        idle: { on: { VERIFY_CHECK: { guard: 'canReCheck', target: 'checking' } } },
        checking: {
          invoke: {
            src: 'verifyCheck',
            input: ({ context }: { context: SignupContext }) => ({
              partnerId: context.partnerId,
              authz: context.authz,
              email: context.values.email,
              password: context.values.password,
            }),
            onDone: [
              {
                guard: 'verifyGotRedirect',
                target: '#signup.redirecting',
                actions: 'assignVerifyRedirect',
              },
              { target: 'verified' },
            ],
            onError: [
              { guard: 'errIsNotVerified', target: 'notYet' },
              { target: 'checkFailed', actions: 'assignVerifyError' },
            ],
          },
        },
        // Verificado, pero el cierre necesita el interstitial de T&C.
        verified: {},
        // El gate sigue rebotando: todavía no nos figura la verificación.
        notYet: { on: { VERIFY_CHECK: { guard: 'canReCheck', target: 'checking' } } },
        // Falla de red/backend: distinta de "todavía no verificaste".
        checkFailed: { on: { VERIFY_CHECK: { guard: 'canReCheck', target: 'checking' } } },
      },
    },
  },
} as const;

export const signupMachine = setup({
  types: {
    context: {} as SignupContext,
    events: {} as SignupEvent,
    input: {} as SignupInput,
  },
  actors: {
    checkEmail: fromPromise<CheckResult, { partnerId: string; email: string }>(async ({ input }) => {
      const res = await apiCheckEmail(input.partnerId, input.email);
      const state: EmailBackendState = res.exists
        ? 'exists'
        : !res.domainDeliverable
          ? 'undeliverable'
          : 'available';
      return { email: input.email, state };
    }),
    verifyCheck: fromPromise<
      VerifyCheckResult,
      { partnerId: string; authz: AuthParamsResult; email: string; password: string }
    >(async ({ input }) => {
      if (!input.authz.ok) {
        // El guard `canReCheck` ya lo impide; esto es defensa en profundidad.
        throw new ApiError('invalid_request_body', 'sin parámetros de autorización', 400);
      }
      const res = await apiLogin({
        email: normalizeEmail(input.email),
        password: input.password,
        partner_id: input.partnerId,
        ...input.authz.params,
      });
      if (isConsentRequired(res)) return { kind: 'verified_needs_consent' };
      return { kind: 'redirect', redirectTo: res.redirect_to };
    }),
    signup: fromPromise<SignupResult, SignupActorInput>(async ({ input }) => {
      const params: SignupParams = { ...input.params };
      // El nonce de navegador (auto-login cross-device) se genera acá porque
      // requiere el side-effect de la cookie. Solo en flujo OAuth.
      if (input.authz.ok) {
        const nonce = makeVerifyNonce();
        setVerifyCookie(nonce);
        params.verify_nonce = nonce;
      }
      return apiSignup(input.partnerId, params);
    }),
  },
  guards: {
    emailCheckable: ({ context }) => emailVerdictOf(context).kind === 'unverified',
    gateInvalid: ({ context }) => context.gateStatus === 'invalid',
    gateNeedBackend: ({ context }) => context.gateStatus === 'need-backend',
    gateReadyPlans: ({ context }) => context.gateStatus === 'ready' && context.plansEnabled,
    gateReadyNoPlans: ({ context }) => context.gateStatus === 'ready' && !context.plansEnabled,
    hasSelectedPlan: ({ context }) => context.selectedPlan !== null,
    plansEnabled: ({ context }) => context.plansEnabled,
    signupHasRedirect: ({ event }) => Boolean((event as { output?: SignupResult }).output?.redirect_to),
    signupHasPayToken: ({ event }) => {
      const o = (event as { output?: SignupResult }).output;
      return Boolean(o?.requires_payment && o?.pay_token);
    },
    errIsEmailTaken: ({ event }) => isEmailTakenError((event as { error?: unknown }).error),
    // El re-chequeo reusa /auth/login, que exige los params del flow OAuth.
    // Sin ellos (alta directa en el portal, sin app que la haya originado) no
    // hay a dónde volver: el botón no se ofrece.
    canReCheck: ({ context }) => context.authz.ok && context.values.password.length > 0,
    verifyGotRedirect: ({ event }) =>
      (event as { output?: VerifyCheckResult }).output?.kind === 'redirect',
    errIsNotVerified: ({ event }) => {
      const err = (event as { error?: unknown }).error;
      return err instanceof ApiError && err.code === 'email_not_verified';
    },
  },
  actions: {
    assignConfig: assign(({ event }) => {
      if (event.type !== 'CONFIG_LOADED') return {};
      return { plansEnabled: event.plansEnabled, requiredFavorites: event.requiredFavorites };
    }),
    assignField: assign(({ context, event }) => {
      if (event.type !== 'SET_FIELD') return {};
      const values = { ...context.values, [event.field]: event.value };
      const fieldErrors = { ...context.fieldErrors };
      delete fieldErrors[event.field];
      // Editar el email oculta objeciones (y limpia el warning) hasta el próximo
      // blur/submit.
      const isEmail = event.field === 'email' || event.field === 'email_confirm';
      const emailTouched = isEmail ? false : context.emailTouched;
      const emailWarning = isEmail ? null : context.emailWarning;
      return { values, fieldErrors, emailTouched, emailWarning };
    }),
    assignToggle: assign(({ context, event }) => {
      if (event.type !== 'TOGGLE') return {};
      const values = { ...context.values, [event.field]: event.value };
      const fieldErrors = { ...context.fieldErrors };
      if (event.field === 'terms_accepted') delete fieldErrors.terms;
      return { values, fieldErrors };
    }),
    assignFavorite: assign(({ context, event }) => {
      if (event.type !== 'SET_FAVORITE') return {};
      return {
        values: {
          ...context.values,
          favorites: { ...context.values.favorites, [event.favoriteTypeId]: event.value },
        },
        // Elegir/editar un favorito limpia su marca de "falta" (como los campos).
        missingFavorites: context.missingFavorites.filter((id) => id !== event.favoriteTypeId),
      };
    }),
    touchEmail: assign({ emailTouched: true }),
    applyTypoFix: assign(({ context }) => {
      const verdict = emailVerdictOf(context);
      if (verdict.kind !== 'typo') return {};
      const fixed = verdict.suggestion.fixedEmail;
      const fieldErrors = { ...context.fieldErrors };
      delete fieldErrors.email;
      delete fieldErrors.email_confirm;
      return {
        values: { ...context.values, email: fixed, email_confirm: fixed },
        typoDismissedFor: null,
        emailTouched: true,
        fieldErrors,
      };
    }),
    keepTyped: assign(({ context }) => ({
      typoDismissedFor: normalizeEmail(context.values.email),
      emailTouched: true,
    })),
    cacheEmailResult: assign(({ context, event }) => {
      const out = (event as { output?: CheckResult }).output;
      if (!out) return {};
      return { emailCache: { ...context.emailCache, [out.email]: out.state } };
    }),
    assignGate: assign(({ context }) => {
      const g = evaluateGate(context);
      return {
        gateStatus: g.status,
        fieldErrors: g.fieldErrors,
        formError: g.formError,
        showRecoverLink: g.showRecoverLink,
        firstErrorField: g.firstErrorField,
        missingFavorites: g.missingFavorites,
      };
    }),
    bumpScroll: assign(({ context }) => ({ scrollNonce: context.scrollNonce + 1 })),
    // FAIL-CLOSED: el check no pudo resolver → warning (ámbar), NO error de campo.
    // No pinta rojo ni va al banner; se muestra como ítem ámbar + marco de warning.
    setUnknownEmailError: assign(({ context }) => ({
      emailWarning: EMAIL_UNKNOWN_MSG,
      firstErrorField: 'email' as FieldKey,
      scrollNonce: context.scrollNonce + 1,
      emailTouched: true,
    })),
    assignSelectedPlan: assign(({ event }) => {
      if (event.type !== 'SELECT_PLAN') return {};
      return { selectedPlan: event.plan, formError: null };
    }),
    clearFormError: assign({ formError: null, showRecoverLink: false }),
    assignRedirect: assign(({ event }) => ({
      redirectTo: (event as unknown as { output: SignupResult }).output.redirect_to ?? null,
    })),
    assignVerifyRedirect: assign(({ event }) => {
      const out = (event as unknown as { output: VerifyCheckResult }).output;
      return { redirectTo: out.kind === 'redirect' ? out.redirectTo : null };
    }),
    assignVerifyError: assign(({ event }) => {
      const err = (event as { error?: unknown }).error;
      return {
        verifyError:
          err instanceof ApiError && err.message
            ? err.message
            : 'No pudimos comprobarlo ahora. Probá de nuevo en un momento.',
      };
    }),
    assignPayToken: assign(({ event }) => ({
      payToken: (event as unknown as { output: SignupResult }).output.pay_token ?? null,
    })),
    assignSubmitted: assign(({ context, event }) => {
      const out = (event as unknown as { output: SignupResult }).output;
      return {
        submittedEmail: context.values.email,
        pendingPaidPlan: out.requires_payment ? context.selectedPlan : null,
      };
    }),
    applyEmailTakenError: assign(({ context }) => {
      const normEmail = normalizeEmail(context.values.email);
      return {
        // El server confirmó que existe → poblamos el cache para que EmailStatus
        // muestre la oferta de login inline, además del banner de arriba.
        emailCache: { ...context.emailCache, [normEmail]: 'exists' as EmailBackendState },
        fieldErrors: { email: EMAIL_EXISTS_MSG } as Partial<Record<FieldKey, string>>,
        formError: 'Ya tenés una cuenta',
        showRecoverLink: true,
        firstErrorField: 'email' as FieldKey,
        scrollNonce: context.scrollNonce + 1,
        emailTouched: true,
      };
    }),
    applyCreateError: assign(({ event }) => {
      const err = (event as { error?: unknown }).error;
      const code = err instanceof ApiError ? err.code : 'unknown';
      const message = err instanceof ApiError ? err.message : undefined;
      const mapped = mapSignupError(code, message);
      return { formError: mapped.text, showRecoverLink: mapped.showRecoverLink };
    }),
    resetForm: assign(({ context }) => ({
      values: initialFormValues(),
      emailCache: {},
      typoDismissedFor: null,
      emailTouched: false,
      fieldErrors: {},
      formError: null,
      showRecoverLink: false,
      firstErrorField: null,
      missingFavorites: [],
      emailWarning: null,
      scrollNonce: context.scrollNonce + 1,
      gateStatus: 'idle' as GateStatus,
      selectedPlan: null,
      submittedEmail: null,
      pendingPaidPlan: null,
      redirectTo: null,
      payToken: null,
    })),
  },
}).createMachine(signupMachineConfig);
